import { Millwright } from '@/apis/millwrights/types/millwrightsApi';
import { Skill } from '@/apis/skills/types/skillsApi';
import { FC, useMemo } from 'react';

import {
  BoxAppstore,
  Chip,
  Icon,
  Stack,
  Tooltip,
  Typography,
} from '@data-products-and-ai/react-components';
import {
  CollapsibleChipList,
  DataItem,
  DataItems,
  LabeledIcon,
  SemiCircleProgressBar,
} from '@/components';
import { MillwrightLatestObservation } from '@/pages/Millwrights/[ID]/components';
import {
  calculateAge,
  formatDateString,
  getRiskColor,
  getRiskLabel,
  isPastDate,
} from '@/utils';
import { getUSStateName } from '@/types';

import '@/pages/Millwrights/[ID]/components/MillwrightDetails/MillwrightDetails.scss';

const SKILLS_TOOLTIP_TEXT = `Skills & Certifications

This list shows the millwright’s current skills and certifications.

Certifications highlighted in red are expired and may need to be reviewed or retaken.

Keeping certifications up to date helps ensure safe work assignment and compliance.`;

const MILLWRIGHT_TOOLTIP_TEXT = `Millwright Details

This section displays key information about the millwright, including age, job title, home location, employee number, and the most recent performance evaluation.

If an evaluation is not available for the current outage, the latest evaluation from a previous outage is shown.`;

const getSkillId = (skill: Skill, index?: number) =>
  `${skill?.certId ?? skill.proficiency}-${skill?.certDate}-${index}`;

const getSkillName = (skill: Skill) => skill.proficiency ?? skill.name;

const getTooltip = (skill: Skill) => {
  const tooltipItems = [
    ...(skill.expirationDate && new Date(skill.expirationDate) < new Date()
      ? [
          {
            key: 'expirationMessage',
            label: 'Expiration message',
            single: true,
            hideLabel: true,
            value: (
              <Stack gap={8} direction="row">
                <Icon icon="IconInformation" color="var(--color-error)" />
                <Typography tag="textdefault" color="var(--color-error)">
                  Certification expired
                </Typography>
              </Stack>
            ),
          },
        ]
      : []),
    {
      key: 'type',
      label: 'Skill type',
      single: true,
      value: skill.skill,
    },
    {
      key: 'date',
      label: 'Certification date',
      value: skill.certDate
        ? new Date(skill.certDate).toLocaleDateString()
        : '-',
    },
    {
      key: 'expirationDate',
      label: 'Expiration date',
      value: skill.expirationDate
        ? new Date(skill.expirationDate).toLocaleDateString()
        : '-',
    },
    ...(skill.notes
      ? [
          {
            key: 'notes',
            label: 'Notes',
            value: skill.notes,
            single: true,
          },
        ]
      : []),
  ];

  return <DataItems items={tooltipItems} mode="light" />;
};

const getSkillColor = (skill: Skill) =>
  isPastDate(skill.expirationDate ?? '') ? 'error' : 'primary';

interface MillwrightDetailsProps {
  outageId: string;
  millwright: Millwright;
  skills: Skill[];
}

export const MillwrightDetails: FC<MillwrightDetailsProps> = ({
  outageId,
  millwright,
  skills,
}) => {
  const age = calculateAge(millwright.birthday);
  const state = getUSStateName(millwright.state);
  const location = `${millwright.city}, ${state}`;

  const dataItems = useMemo(
    () => [
      {
        key: 'id',
        label: 'Employee Nr.',
        inline: true,
        single: true,
        value: (
          <Stack direction="row" gap={16} wrap>
            <Typography color="var(--color-neutral)" tag="textsmall">
              {millwright.empId}
            </Typography>
            <Chip
              label={`${age} years old`}
              color="primary"
              chipType="thin"
              size="extra-small"
            />
          </Stack>
        ),
      },
      {
        key: 'jobTitleEx',
        label: 'Job title',
        single: true,
        inline: true,
        value: millwright?.jobTitleEx ?? 'N/A',
      },
      {
        key: 'state',
        label: 'Home location',
        single: true,
        value: <LabeledIcon icon="IconHome" label={location} />,
      },
      {
        key: 'observation',
        hideLabel: true,
        value: (
          <MillwrightLatestObservation
            millwrightId={millwright.empId}
            outageId={Number(outageId)}
          />
        ),
        single: true,
      },
    ],
    [millwright, location, age],
  );

  return (
    <BoxAppstore>
      <div className="millwright">
        <div className="millwright__info-details">
          <Stack direction="row" gap={8}>
            <Typography color="var(--color-neutral)" tag="textdefault_strong">
              Millwright Details
            </Typography>
            <Tooltip title={MILLWRIGHT_TOOLTIP_TEXT} arrow>
              <Icon
                icon="IconInformation"
                color="var(--color-default)"
                iconSize={0.8}
              />
            </Tooltip>
          </Stack>
          <DataItems items={dataItems} />
        </div>
        <div className="millwright__risk">
          <Stack gap={32}>
            <Typography tag="textdefault_strong">
              Overall Personal Risk
            </Typography>
            <SemiCircleProgressBar
              progress={millwright?.globalPercentile ?? 0}
              size={200}
              strokeWidth={12}
              color={getRiskColor(millwright?.globalPercentile ?? 0)}
              labelText={getRiskLabel(millwright?.globalPercentile ?? 0)}
              mode="label"
              showLabel
            />
            {millwright.riskDate && (
              <DataItem
                key="risk"
                label="Risk Date"
                inline
                single
                value={
                  <Typography tag="textextrasmall_strong">
                    {formatDateString(millwright.riskDate, 'MMMM Do, YYYY')}
                  </Typography>
                }
              />
            )}
          </Stack>
        </div>
        <div className="millwright__skills">
          <Stack
            inline
            verticalAlign="center"
            direction="row"
            gap={8}
            horizontalAlign="left"
          >
            <Typography color="var(--color-black)" tag="textdefault_strong">
              Skills
            </Typography>
            <Tooltip title={SKILLS_TOOLTIP_TEXT} arrow>
              <Icon
                icon="IconInformation"
                color="var(--color-default)"
                iconSize={0.8}
              />
            </Tooltip>
          </Stack>

          <CollapsibleChipList
            items={skills}
            tooltipColor="light"
            maxVisible={15}
            getKey={getSkillId}
            getLabel={getSkillName}
            getTooltip={getTooltip}
            getColor={getSkillColor}
          />
        </div>
      </div>
    </BoxAppstore>
  );
};
